import React from "react";
import { Link } from "react-router-dom";
import { strings } from "../../resources/strings";
import { langAttribute } from "../../utils/questionUtils";
import { ResourceViewModel } from "./resourceUtils";
import { DetailHistoryState } from "./resourceListState";

type ResourceCardProps = {
  resource: ResourceViewModel;
  /** The resource's detail page. */
  to: string;
  linkState?: DetailHistoryState;
  /** Runs before the detail page opens. */
  onOpen?: () => void;
  linkRef?: (element: HTMLAnchorElement | null) => void;
};

/**
 * One resource in the list. Empty fields are omitted, as on iOS. The title
 * link stretches over the whole card, so the card is one target and one tab
 * stop whose name is just the title.
 */
const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  to,
  linkState,
  onOpen,
  linkRef,
}) => (
  <article className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:border-primary">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="m-0 text-lg font-semibold text-primary">
        <Link
          to={to}
          state={linkState}
          // Only a plain click stays in this tab; a modifier or middle click
          // opens a new one, and this list should not then move focus.
          onClick={(event) => {
            if (
              event.button === 0 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              onOpen?.();
            }
          }}
          ref={linkRef}
          // The ring is drawn on the overlay, so it outlines the whole card in
          // every browser (no :has() needed).
          className="text-primary no-underline hover:underline focus:outline-none after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-primary"
        >
          {resource.title}
        </Link>
      </h2>
      {resource.categoryLabel && (
        <p
          className="m-0 text-sm text-gray-700"
          lang={langAttribute(resource.categoryLocale)}
        >
          {resource.categoryLabel}
        </p>
      )}
    </div>
    {resource.description && (
      // Clamped here; the full text stays in the DOM for screen readers and
      // is shown unclamped on the detail page.
      <p className="mt-2 mb-0 line-clamp-3 whitespace-pre-line text-gray-700">
        {resource.description}
      </p>
    )}
    {resource.keywords.length > 0 && (
      <p className="mt-2 mb-0 text-sm text-gray-700">
        <span className="sr-only">{strings.resources.keywordsLabel} </span>
        {resource.keywords.join(", ")}
      </p>
    )}
  </article>
);

export default ResourceCard;
